import styled from '@emotion/styled'
import { downloadFileWithReportKey } from '@faceup/crypto'
import UntitledIcon from '@faceup/icons/src/UntitledIcons/UntitledIcon'
import { ulEyeOff } from '@faceup/icons/ulEyeOff'
import { usAttachment01 } from '@faceup/icons/usAttachment01'
import { TextUrlfy, UserAvatar } from '@faceup/ui'
import { Space, Tooltip, Typography, useUiBaseConfig } from '@faceup/ui-base'
import { captureException } from '@sentry/react'
import moment from 'moment-timezone'
import { memo, useContext } from 'react'
import { FormattedMessage, FormattedMessage as FormattedMessageDef } from 'react-intl'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { useMarkMessagesAsRead } from '../../hooks/useMarkMessagesAsRead'
import { useSuspenseChatMessage } from '../../hooks/useSuspenseChatMessage'
import { defineMessages, useIntl } from '../../TypedIntl'
import { ChatAttachmentList } from '../AttachmentList'
import { TextToRedact, UsernameAvatarRedact, UsernameRedact } from '../Redaction'
import { RelativeMessagesContext } from './RelativeMessagesContext'

const fragments = {
  FollowUpChatMessage_followUpActivityNode: graphql(`
    fragment FollowUpChatMessage_followUpActivityNode on CompanyReportFollowUpComment {
      id
      author {
        ... on CompanyVictim {
          id
        }

        ... on Member {
          id
          name
          profileImageUrl
        }
      }
      translation(
        sourceLanguage: $sourceLanguage
        targetLanguage: $targetLanguage
      ) {
        ciphertext
        nonce
        sourceLanguage
        targetLanguage
      }
      isAuthorRedacted
      isNameHidden
      deletedAt
      readByMemberIds
      isMessageReadByVictim
      case {
        id
        ... on CompanyReport {
          translation {
            id
            body
            bodyNonce
          }
        }
        encryptionKey
        organizationalUnit {
          id
          organizationalUnitName
          mother {
            id
          }
        }
      }
      attachments {
        id
        name
        url
        mimetype
      }
      createdAt

      ...UseMarkMessagesAsReadFragments_comment
    }
  `),
}

const messages = defineMessages({
  anonymous: 'Shared.chat.anonymous',
  notSeenYet: 'Shared.chat.notSeenYet',
  read: 'Shared.chat.read',
  anonymizedUser: 'Report.chat.anonymizedUser',
})

type Props = {
  orientation: 'left' | 'right'
  message: FragmentType<typeof fragments.FollowUpChatMessage_followUpActivityNode>
  fileDownloadHandler?: (blob: Blob, name: string) => Promise<void>
  onMessageSeen?: () => void
}

const ChatMessage = ({
  orientation,
  message: _message,
  fileDownloadHandler,
  onMessageSeen,
}: Props) => {
  const message = getFragmentData(fragments.FollowUpChatMessage_followUpActivityNode, _message)
  const relativeMessages = useContext(RelativeMessagesContext)
  const { token } = useUiBaseConfig()
  const { formatMessage } = useIntl()
  const isFollowUp = relativeMessages.type === 'Victim'

  const author = message?.author
  const authorType = author?.__typename
  const report = message.case?.__typename === 'CompanyReport' ? message.case : null

  const isViewersMessage =
    (authorType === 'CompanyVictim' && isFollowUp) || (authorType === 'Member' && !isFollowUp)

  const { ref } = useMarkMessagesAsRead({
    message,
    onCompleted: () => onMessageSeen?.(),
  })

  const { text, victimName } = useSuspenseChatMessage({
    ciphertext: message?.translation?.ciphertext ?? '',
    nonce: message?.translation?.nonce ?? '',
    encryptionKey: message?.case?.encryptionKey ?? '',
    // Only reporter-authored messages need the (encrypted) name decrypted;
    // member names come straight from the GraphQL data.
    victimReport:
      authorType === 'CompanyVictim' && report
        ? {
            body: report.translation?.body ?? '',
            bodyNonce: report.translation?.bodyNonce ?? '',
          }
        : null,
  })

  const resolveAuthorName = () => {
    if (author?.__typename === 'Member') {
      if (message?.isAuthorRedacted) {
        return formatMessage(messages.anonymizedUser)
      }
      return author.name ?? message.case?.organizationalUnit?.organizationalUnitName ?? ''
    }
    if (author?.__typename === 'CompanyVictim') {
      return victimName || formatMessage(messages.anonymous)
    }
    return ''
  }
  const authorName = resolveAuthorName()

  return (
    <MessageWrapper isRightAligned={orientation === 'right'}>
      <Wrapper isRightAligned={orientation === 'right'}>
        <div
          style={{
            background:
              message?.author?.__typename === 'CompanyVictim' ? token.primary1 : '#f0f3f4',
            whiteSpace: 'pre-line',
            overflow: 'hidden',
            wordBreak: 'break-word',
            borderRadius: orientation === 'right' ? '12px 12px 2px' : '12px 12px 12px 2px',
          }}
          data-test='message-text'
        >
          <MessageHeaderSender>
            {message?.isNameHidden ? (
              <Tooltip
                title={
                  relativeMessages.type === 'Admin' && (
                    <FormattedMessageDef {...relativeMessages.nameHidden} />
                  )
                }
                placement='top'
              >
                <Space>
                  <UsernameRedact
                    type={
                      message?.author?.__typename === 'Member'
                        ? {
                            type: 'chatMemberUsername',
                            id: message.author?.id ?? '',
                          }
                        : {
                            type: 'sender',
                          }
                    }
                    text={authorName}
                  >
                    {authorName}
                  </UsernameRedact>
                  <UntitledIcon icon={ulEyeOff} />
                </Space>
              </Tooltip>
            ) : (
              <UsernameRedact
                type={
                  message?.author?.__typename === 'Member'
                    ? {
                        type: 'chatMemberUsername',
                        id: message.author?.id ?? '',
                      }
                    : {
                        type: 'sender',
                      }
                }
                text={authorName}
              >
                <span className='sensitive'>{authorName}</span>
              </UsernameRedact>
            )}
          </MessageHeaderSender>
          {text && (
            <MessageText className='sensitive'>
              <TextToRedact
                type={{
                  type: 'chatMessages',
                  id: message?.id ?? '',
                }}
                text={text}
              >
                <TextUrlfy text={text} />
              </TextToRedact>
            </MessageText>
          )}
          <ChatAttachmentList
            attachments={message?.attachments ?? []}
            icons={{
              attachment: usAttachment01,
            }}
            download={async attachment => {
              const result = await downloadFileWithReportKey(
                attachment,
                message?.case?.encryptionKey ?? '',
                fileDownloadHandler
              )
              if (result.isErr()) {
                captureException(result.error.exception, {
                  tags: { type: 'file_decryption' },
                  extra: {
                    flow: 'chat-attachment-download',
                    messageId: message?.id,
                    caseId: message?.case?.id,
                    attachmentId: attachment.id,
                    fileName: attachment.name,
                    mimetype: attachment.mimetype,
                  },
                })
              }
              return result
            }}
          />
        </div>
        <MessageFooter isRightAligned={orientation === 'right'}>
          {isViewersMessage && (
            <>
              <MessageFooterContent>
                {(authorType === 'Member' && message?.isMessageReadByVictim) ||
                (authorType === 'CompanyVictim' && message?.readByMemberIds.length) ? (
                  <FormattedMessage {...messages.read} />
                ) : (
                  <FormattedMessage {...messages.notSeenYet} />
                )}
              </MessageFooterContent>
              <Divider />
            </>
          )}
          <MessageFooterContent
            // Should be referenced at bottom of the message.
            // Long messages may not fit entirely within the visible area and might not be marked as read.
            ref={ref}
          >
            {moment(message?.createdAt).format('LLL')}
          </MessageFooterContent>
        </MessageFooter>
      </Wrapper>
      <UsernameAvatarRedact
        type={
          message?.author?.__typename === 'Member'
            ? {
                type: 'chatMemberUsername',
                id: message.author?.id ?? '',
              }
            : {
                type: 'sender',
              }
        }
        text={authorName}
      >
        <AvatarWrapper>
          <UserAvatar user={{ ...message?.author, name: authorName }} />
        </AvatarWrapper>
      </UsernameAvatarRedact>
    </MessageWrapper>
  )
}

const AvatarWrapper = styled.span`
  .ant-avatar {
    @media print {
      display: none;
    }

    background: #0e9af7;
    min-width: 32px;
  }
`

const Wrapper = styled.div<{ isRightAligned: boolean }>`
  display: inline-flex;
  flex-flow: nowrap column;
  padding-bottom: 20px;
  max-width: 80%;
`

const MessageText = styled.div`
  padding: 2px 16px 8px 16px;
  max-width: 600px;
`

const MessageFooterContent = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: #688699;
`

const MessageFooter = styled.div<{ isRightAligned: boolean }>`
  display: flex;
  flex-flow: ${({ isRightAligned }) => (isRightAligned ? 'row-reverse' : 'row')} nowrap;
  gap: 4px;
  justify-content: flex-start;
  align-items: center;
  padding: 4px;
`

const Divider = styled.div`
  color: #688699;
  padding: 0 2px;
  &:before {
    content: '•';
  }
`
const MessageWrapper = styled.div<{ isRightAligned: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  flex-direction: ${({ isRightAligned }) => (isRightAligned ? 'row' : 'row-reverse')};
  margin-inline-end: ${({ isRightAligned }) => (isRightAligned ? '12px' : '0')};
  gap: 8px;
`

const MessageHeaderSender = styled(Typography.Text)`
  display: block;
  padding: 8px 16px 2px 16px;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
`

export default memo(ChatMessage)
